/**
 * nSure SDK Integration for Chargeback Protection
 * Device ID collection for fraud detection
 * 
 * According to Coinflow documentation, the nSure SDK should be loaded on every page
 * to gather device and session information throughout the user's shopping experience.
 * This enables better fraud detection and chargeback protection.
 */

declare global {
  interface Window {
    nSureSDK?: {
      init: (config: { appId: string; partnerId: string }) => void;
      getDeviceId: () => string | null;
    };
    nSureAsyncInit?: (deviceId: string) => void;
  }
}

/**
 * Initialize nSure SDK - should be called on app load
 * This allows the SDK to gather device and session information throughout the user's visit
 */
export function initializeNSure() {
  // Check if SDK is already loaded
  if (window.nSureSDK) {
    return;
  }

  // Check if script is already being loaded
  if (document.querySelector('script[src="https://sdk.nsureapi.com/sdk.js"]')) {
    return;
  }

  // Set up async initialization callback BEFORE loading script
  // This matches Coinflow's documentation pattern
  window.nSureAsyncInit = function(deviceId: string) {
    window.nSureSDK = window.nSureSDK || {};
    window.nSureSDK.init({
      appId: '9JBW2RHC7JNJN8ZQ', // Same for sandbox and production
      partnerId: import.meta.env.VITE_COINFLOW_ENVIRONMENT === 'production' ? 'GOLDMACHINE' : 'COINFTEST',
    });
  };

  // Load nSure SDK script
  const script = document.createElement('script');
  script.src = 'https://sdk.nsureapi.com/sdk.js';
  script.async = true;
  document.head.appendChild(script);
}

/**
 * Get device ID from nSure SDK
 * Returns null if SDK is not yet loaded
 */
export function getDeviceId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  // Ensure SDK is initialized (should already be done on app load)
  if (!window.nSureSDK) {
    // Fallback: initialize if not already done
    initializeNSure();
    // Return null since SDK needs time to load
    return null;
  }

  if (window.nSureSDK.getDeviceId) {
    return window.nSureSDK.getDeviceId();
  }

  return null;
}

